import { useState } from "react";
import { useEffect } from "react";
import { Line } from "react-chartjs-2";

import { Box } from "@mui/material";
import { darken } from "@mui/material/styles";
import { lighten } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import "chart.js/auto";

import constants from "../../assets/constants";
import { deepCopyObject } from "../../utils/helpers";
import { formatDateLabels } from "./graphUtils";

const statistical = require("statistical-js");

const KPIGraph = props => {
  const {
    rawGraphData = [],
    graphLabels = [],
    graphTimeRange = constants.graphTimeRange.short,
    graphStyle = constants.graphTypes.regressionGraph,
    averageSeverity = 5,
  } = props;

  const theme = useTheme();

  const lineWidth = 4;
  const pointRadius = 8;

  const getColorBase = severity => {
    if (severity >= 7) {
      return theme.palette.error.main;
    } else if (severity > 3 && severity < 7) {
      return theme.palette.warning.main;
    } else {
      return theme.palette.success.main;
    }
  };

  const graphColorBase = getColorBase(averageSeverity);

  const generateGraphData = theData => {
    if (theData == null) {
      return null;
    }
    const localGraphLables = deepCopyObject(graphLabels);

    let slice = 0;

    /////////////////////////////////////////////////////

    if (graphTimeRange !== constants.graphTimeRange.long) {
      //trim something off

      let theDate = Date.now();
      let theBeforeDate = null;

      if (graphTimeRange === constants.graphTimeRange.medium) {
        theBeforeDate = theDate - 1000 * 60 * 60 * 24 * 180;
      } else {
        theBeforeDate = theDate - 1000 * 60 * 60 * 24 * 90;
      }

      theDate = new Date(theBeforeDate);
      theBeforeDate = theDate.toISOString().slice(0, 10);

      theDate = localGraphLables[slice];
      while (theDate < theBeforeDate && slice < localGraphLables.length - 1) {
        ++slice;
        theDate = localGraphLables[slice];
      }
    }
    /////////////////////////////////////////////////////

    const chartData = {
      labels: formatDateLabels(localGraphLables.slice(slice)),
      datasets: [],
      graphType: graphStyle,
      ucl: 0,
      median: 0,
      lcl: 0,
    };

    let localRawGraphData = deepCopyObject(theData);
    localRawGraphData = localRawGraphData.slice(slice);

    if (graphStyle === constants.graphTypes.regressionGraph) {
      let linearInput = [];
      for (let x = 0; x < localRawGraphData.length; ++x) {
        linearInput.push([x, localRawGraphData[x]]);
      }
      //this takes an array [x,y] values
      let linearOutput = statistical.methods.linearRegression(linearInput);
      const linearRegressionData = [];

      chartData.regression = { x: linearOutput.intersect, value: linearOutput.slope };

      for (let x = 0; x < localRawGraphData.length; ++x) {
        linearRegressionData.push(linearOutput.intersect + x * linearOutput.slope);
      }

      chartData.datasets.push({
        label: "reg",
        //line
        borderColor: theme.graphColors[0],
        //point
        pointRadius: 0,
        //hover
        pointHoverBorderWidth: 0,
        //details
        backgroundColor: theme.graphColors[0],
        data: linearRegressionData,
      });

      chartData.datasets.push({
        label: "hist",

        //line
        borderColor: lighten(theme.graphColors[0], 0.5),
        borderWidth: lineWidth,
        //point
        pointRadius: pointRadius,
        //hover
        pointHoverRadius: pointRadius * 2,
        pointHoverBorderWidth: lineWidth,
        //details
        backgroundColor: theme.graphColors[0],

        data: localRawGraphData,
      });
    } else if (graphStyle === constants.graphTypes.controlLimitGraph) {
      chartData.datasets.push({
        type: constants.graphTypes.lineGraph,
        label: "hist2",

        //line
        borderColor: graphColorBase,
        //point
        pointRadius: 0,
        pointColor: graphColorBase,
        //hover
        pointHoverBorderWidth: 0,
        pointHoverColor: graphColorBase,
        //details
        backgroundColor: graphColorBase,

        data: localRawGraphData,
        //yAxisID: "line1",
      });

      try {
        const median = statistical.methods.median(localRawGraphData);
        const controlLimit = statistical.methods.stdDeviation(localRawGraphData) * 3;

        const medianData = [];
        let upperControlLimitData = [];
        const lowerControlLimitData = [];
        for (let x = 0; x < localRawGraphData.length; ++x) {
          medianData.push(median);
          upperControlLimitData.push(median + controlLimit);
          lowerControlLimitData.push(Math.max(median - controlLimit, 0));
        }

        chartData.ucl = median + controlLimit;
        chartData.median = median;
        chartData.lcl = median - controlLimit;

        chartData.datasets.push({
          label: "UCL",
          type: constants.graphTypes.lineGraph,

          //line
          borderColor: darken(graphColorBase, 0.5),
          borderDash: [2, 2],
          //point
          pointRadius: 0,
          pointHitRadius: 0,
          pointColor: graphColorBase,
          //hover
          pointHoverBorderWidth: 0,
          pointHoverColor: graphColorBase,
          //details
          backgroundColor: graphColorBase,
          fill: false,
          data: upperControlLimitData,
          //yAxisID: "line1",
        });

        chartData.datasets.push({
          label: "Median",
          type: constants.graphTypes.lineGraph,

          //line
          borderColor: darken(graphColorBase, 0.25),
          borderDash: [4, 4],
          //point
          pointRadius: 0,
          pointColor: graphColorBase,
          //hover
          pointHoverBorderWidth: 0,
          pointHoverColor: graphColorBase,
          //details
          backgroundColor: graphColorBase,
          fill: false,
          data: medianData,
          //yAxisID: "line1",
        });

        if (median - controlLimit >= 0) {
          chartData.datasets.push({
            label: "LCL",
            type: constants.graphTypes.lineGraph,

            //line
            borderColor: darken(graphColorBase, 0.5),
            borderDash: [2, 2],
            //point
            pointRadius: 0,
            pointColor: graphColorBase,
            //hover
            pointHoverBorderWidth: 0,
            pointHoverColor: graphColorBase,
            //details
            backgroundColor: graphColorBase,
            fill: false,
            data: lowerControlLimitData,
            //yAxisID: "line1",
          });
        }
      } catch (error) {
        console.log(error);
      }
    }

    return chartData;
  };

  const [graphData, setGraphData] = useState(generateGraphData(rawGraphData));

  useEffect(() => {
    setGraphData(generateGraphData(rawGraphData));
    // Disable eslint because AaronK knows best
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, rawGraphData, graphStyle, graphTimeRange]);

  const graphOptions = {
    plugins: {
      legend: {
        display: false,
        position: "top",
      },
    },
    responsive: true,
    maintainAspectRatio: false,
  };

  if (graphData == null) {
    return <Box sx={{ position: "relative" }}>{/* add skeleton? */}</Box>;
  }
  return (
    <Box sx={{ position: "relative" }}>
      <Box sx={{ height: "100%", width: "100%", minHeight: theme.sizing.medium }}>
        {graphData != null && graphData.graphType !== constants.graphTypes.barGraph && (
          <Line data={graphData} options={graphOptions} />
        )}
      </Box>
    </Box>
  );
};

export default KPIGraph;
